import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../components/Table";
import { useUserSensorReadingService } from "../../services/sensorForuser";

function isPrimitive(v) {
  return (
    v === null ||
    v === undefined ||
    typeof v === "string" ||
    typeof v === "number" ||
    typeof v === "boolean"
  );
}

function formatKey(key) {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (!isPrimitive(value)) return "—";
  const k = String(key).toLowerCase();
  if (k.includes("date") || k.includes("timestamp") || k.endsWith("at")) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  if (k === "totalflow") return `${value} m3`;
  if (k === "currentflow") return `${value} m3/h`;
  return String(value);
}

function parseHistory(history) {
  if (!Array.isArray(history)) return [];
  const rows = [];
  for (const raw of history) {
    try {
      const parsed = JSON.parse(raw);
      rows.push(parsed);
    } catch {
      // ignore invalid
    }
  }
  return rows;
}

const WATER_KEYS = [
  "waterLevel",
  "totalFlow",
  "currentFlow",
  "cod",
  "bod",
  "ph",
  "tds",
  "temperature",
  "ec",
  "toc",
  "tss",
  "turbidity",
  "chlorine",
  "dissolvedOxygen",
  "orp",
];

const AIR_KEYS = ["pm25", "pm10", "sox", "nox"];

const SensorReadingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getSensorReadingById } = useUserSensorReadingService();

  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setError(null);
        const res = await getSensorReadingById(id, {
          historyLimit: 5,
          dailyFlowLimit: 30,
        });
        if (!mounted) return;
        setReading(res?.data || null);
      } catch (e) {
        if (!mounted) return;
        setError("Failed to load sensor details");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [getSensorReadingById, id]);

  const metaRows = useMemo(() => {
    if (!reading) return [];
    const metaKeys = [
      "monitoringUnitId",
      "sensorId",
      "readingTimestamp",
      "createdAt",
      "updatedAt",
    ];
    return metaKeys
      .filter((k) => k in reading)
      .map((k) => ({
        key: k,
        label: formatKey(k),
        value: formatValue(k, reading[k]),
      }));
  }, [reading]);

  const waterRows = useMemo(() => {
    if (!reading) return [];
    return WATER_KEYS.filter((k) => k in reading)
      .map((k) => ({
        key: k,
        label: formatKey(k),
        value: formatValue(k, reading[k]),
      }))
      .filter((r) => r.value !== "—");
  }, [reading]);

  const airRows = useMemo(() => {
    if (!reading) return [];
    return AIR_KEYS.filter((k) => k in reading)
      .map((k) => ({
        key: k,
        label: formatKey(k),
        value: formatValue(k, reading[k]),
      }))
      .filter((r) => r.value !== "—");
  }, [reading]);

  const otherRows = useMemo(() => {
    if (!reading) return [];
    const excluded = new Set([
      ...WATER_KEYS,
      ...AIR_KEYS,
      "_id",
      "__v",
      "userId",
      "monitoringUnitId",
      "sensorId",
      "dailyFlowData",
      "readingHistory",
      "readingTimestamp",
      "createdAt",
      "updatedAt",
    ]);
    return Object.keys(reading)
      .filter((k) => !excluded.has(k))
      .filter((k) => isPrimitive(reading[k]))
      .sort()
      .map((k) => ({
        key: k,
        label: formatKey(k),
        value: formatValue(k, reading[k]),
      }))
      .filter((r) => r.value !== "—");
  }, [reading]);

  const dailyFlowData = useMemo(() => {
    const list = reading?.dailyFlowData;
    if (!Array.isArray(list)) return [];
    return list
      .map((d) => ({
        date: d?.date ? new Date(d.date).toLocaleDateString() : "—",
        totalFlow: formatValue("totalFlow", d?.totalFlow),
      }))
      .reverse()
      .slice(0, 30);
  }, [reading]);

  const historyTable = useMemo(() => {
    const rows = parseHistory(reading?.readingHistory);
    if (rows.length === 0) return [];
    const last = rows.slice(-5).reverse();
    return last.map((r) => ({
      timestamp: formatValue(
        "readingTimestamp",
        r.readingTimestamp || r.updatedAt || r.createdAt,
      ),
      waterLevel: r.waterLevel ?? "—",
      currentFlow: formatValue("currentFlow", r.currentFlow),
      ph: r.ph ?? "—",
      pm25: r.pm25 ?? "—",
      pm10: r.pm10 ?? "—",
    }));
  }, [reading]);

  const toCsvValue = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") return JSON.stringify(v);
    const s = String(v);
    const escaped = s.replace(/"/g, '""');
    if (/[",\n]/.test(escaped)) return `"${escaped}"`;
    return escaped;
  };

  const downloadCsv = (rows, filename) => {
    if (!Array.isArray(rows) || rows.length === 0) return;

    const columnsSet = new Set();
    for (const r of rows) {
      for (const key of Object.keys(r || {})) columnsSet.add(key);
    }

    const columns = Array.from(columnsSet).sort((a, b) => {
      if (a === "readingTimestamp") return -1;
      if (b === "readingTimestamp") return 1;
      return a.localeCompare(b);
    });

    const header = columns.map(toCsvValue).join(",");
    const lines = rows.map((r) =>
      columns.map((c) => toCsvValue(r?.[c])).join(",")
    );

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    if (exporting) return;
    try {
      setExporting(true);
      setError(null);

      const fullRes = await getSensorReadingById(id, {
        historyLimit: "all",
        dailyFlowLimit: 0,
      });

      const fullReading = fullRes?.data;
      const rows = parseHistory(fullReading?.readingHistory);

      if (!rows.length) {
        setExporting(false);
        setError("No reading history available to export.");
        return;
      }

      const filename = `sensor-${fullReading?.sensorId || id}-reading-history.csv`;
      downloadCsv(rows, filename);
    } catch (e) {
      setError("Failed to export CSV.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="p-6">Loading sensor details…</div>;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (!reading) {
    return (
      <div className="p-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-700">
          No sensor details found.
          <button
            onClick={() => navigate(-1)}
            className="ml-3 px-3 py-1.5 bg-white border rounded hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const kvColumns = [
    { key: "label", title: "Field" },
    { key: "value", title: "Value" },
  ];

  const historyColumns = [
    { key: "timestamp", title: "Timestamp" },
    { key: "waterLevel", title: "Water Level" },
    { key: "currentFlow", title: "Current Flow (m3/h)" },
    { key: "ph", title: "pH" },
    { key: "pm25", title: "PM2.5" },
    { key: "pm10", title: "PM10" },
  ];

  const flowColumns = [
    { key: "date", title: "Date" },
    { key: "totalFlow", title: "Total Flow (m3)" },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {reading.monitoringUnitId} • {reading.sensorId}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Updated:{" "}
            {formatValue(
              "updatedAt",
              reading.updatedAt || reading.readingTimestamp,
            )}
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          Back
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Identification
        </h2>
        <Table columns={kvColumns} data={metaRows} />
      </div>

      {waterRows.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Water Parameters
          </h2>
          <Table columns={kvColumns} data={waterRows} />
        </div>
      )}

      {airRows.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Air Quality Parameters
          </h2>
          <Table columns={kvColumns} data={airRows} />
        </div>
      )}

      {otherRows.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Other Fields
          </h2>
          <Table columns={kvColumns} data={otherRows} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Daily Flow Data
          </h2>
          {dailyFlowData.length === 0 ? (
            <div className="text-gray-600">No daily flow data.</div>
          ) : (
            <Table columns={flowColumns} data={dailyFlowData} />
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Reading History (latest)
            </h2>
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={exporting || historyTable.length === 0}
              className="px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? "Exporting..." : "Export CSV"}
            </button>
          </div>
          {historyTable.length === 0 ? (
            <div className="text-gray-600">No reading history.</div>
          ) : (
            <Table columns={historyColumns} data={historyTable} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SensorReadingDetail;
